import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatTRY, STATUS_META, STATUS_ORDER } from "@/lib/pricing";
import { generateQuotePdf } from "@/lib/pdf";
import { toast } from "sonner";
import { Package, Truck, Clock, CheckCircle2, Hammer, Plus, MapPin, FileDown } from "lucide-react";

const STATUS_ICON = {
  hazırlanıyor: Clock,
  üretimde: Hammer,
  kargoda: Truck,
  teslim_edildi: CheckCircle2,
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.hazırlanıyor;
  const Icon = STATUS_ICON[status] || Clock;
  return (
    <span
      data-testid="order-status-badge"
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
      style={{ background: meta.bg, color: meta.color }}
    >
      <Icon className="w-3.5 h-3.5" /> {meta.label}
    </span>
  );
}

function Timeline({ status }) {
  const idx = STATUS_ORDER.indexOf(status);
  return (
    <div className="flex items-center gap-1 mt-3">
      {STATUS_ORDER.map((s, i) => {
        const meta = STATUS_META[s];
        const active = i <= idx;
        return (
          <div key={s} className="flex-1 flex items-center gap-1">
            <div
              className="h-1.5 flex-1 rounded-full transition-colors"
              style={{ background: active ? meta.color : "#E6DFD5" }}
            />
          </div>
        );
      })}
    </div>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Orders() {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, []);

  return (
    <div className="min-h-screen bg-atelier">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-4xl font-bold text-stone-900">Siparişlerim</h1>
            <p className="text-stone-600">Tüm siparişleriniz ve teslimat durumları.</p>
          </div>
          <Link to="/tasarim">
            <Button className="rounded-full"><Plus className="w-4 h-4" /> Yeni Tasarım</Button>
          </Link>
        </div>

        {orders === null ? (
          <p className="text-stone-500">Yükleniyor...</p>
        ) : orders.length === 0 ? (
          <div className="card-atelier rounded-2xl p-12 text-center">
            <Package className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-600 mb-4">Henüz siparişiniz yok.</p>
            <Link to="/tasarim">
              <Button className="rounded-full">İlk Dolabınızı Tasarlayın</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="card-atelier rounded-2xl p-6" data-testid={`order-card-${o.orderNumber}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm font-bold text-stone-900">{o.orderNumber}</span>
                      <StatusBadge status={o.status} />
                    </div>
                    <p className="text-xs text-stone-500 mt-1">{fmtDate(o.createdAt)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-2xl font-bold text-primary">{formatTRY(o.totalCost)}</p>
                    {o.paymentStatus === "ödendi" ? (
                      <p className="text-xs text-emerald-600 font-semibold" data-testid={`order-paid-${o.orderNumber}`}>Ödendi</p>
                    ) : o.paymentStatus === "başarısız" ? (
                      <p className="text-xs text-red-600 font-semibold" data-testid={`order-payfailed-${o.orderNumber}`}>Ödeme Başarısız</p>
                    ) : (
                      <p className="text-xs text-amber-600 font-semibold" data-testid={`order-paypending-${o.orderNumber}`}>Ödeme Bekliyor</p>
                    )}
                    <button
                      data-testid={`order-pdf-${o.orderNumber}`}
                      onClick={() =>
                        generateQuotePdf({
                          config: o.config,
                          cost: { items: o.costItems || [], total: o.totalCost },
                          cnc: o.cnc || {},
                          orderNumber: o.orderNumber,
                        }).catch(() => toast.error("PDF oluşturulamadı"))
                      }
                      className="mt-1 inline-flex items-center gap-1 text-xs text-stone-500 hover:text-primary"
                    >
                      <FileDown className="w-3.5 h-3.5" /> Teklif PDF
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
                  <div className="rounded-xl bg-[#F5F0EB] p-3">
                    <p className="text-xs text-stone-500 mb-1">Ölçüler</p>
                    <p className="font-medium text-stone-800">
                      {o.config.width}×{o.config.height}×{o.config.depth} cm
                    </p>
                    <p className="text-xs text-stone-500">{o.config.shelves} raf • {o.config.doorless ? "kapaksız" : o.config.doorStyle === "flap" ? `${o.config.doors} düşer kapak` : o.config.doorLayout === "split3" ? `${o.config.doors * 3} parça kapak` : o.config.doorLayout === "split" ? `${o.config.doors * 2} parça kapak` : `${o.config.doors} kapak`}</p>
                  </div>
                  <div className="rounded-xl bg-[#F5F0EB] p-3">
                    <p className="text-xs text-stone-500 mb-1">Renkler</p>
                    <p className="text-xs text-stone-500">
                      Gövde: {o.config.bodyMaterialLabel ? `${o.config.bodyMaterialLabel} • ` : ""}{o.config.doorColorLabel ? "" : ""}{o.config.bodyColorLabel}
                      <br />
                      Kapak: {o.config.doorMaterialLabel ? `${o.config.doorMaterialLabel} • ` : ""}{o.config.doorColorLabel}
                    </p>
                  </div>
                  <div className="rounded-xl bg-[#F5F0EB] p-3">
                    <p className="text-xs text-stone-500 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Teslimat</p>
                    <p className="font-medium text-stone-800 truncate">{o.shipping?.fullName}</p>
                    <p className="text-xs text-stone-500 truncate">{o.shipping?.city}</p>
                  </div>
                </div>

                <Timeline status={o.status} />
                <div className="flex justify-between text-[11px] text-stone-500 mt-1.5 font-mono">
                  <span>Hazırlanıyor</span>
                  <span>Üretimde</span>
                  <span>Kargoda</span>
                  <span>Teslim</span>
                </div>

                {o.trackingNumber && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl bg-violet-50 border border-violet-200 p-3" data-testid={`order-tracking-${o.orderNumber}`}>
                    <Truck className="w-4 h-4 text-violet-600" />
                    <span className="text-sm text-violet-800">
                      Kargo: <strong>{o.carrier || "Kargo"}</strong> • Takip No:{" "}
                      <span className="font-mono font-semibold">{o.trackingNumber}</span>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
